import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Dialog,
  IconButton,
  Typography,
} from '@material-ui/core';
import CallIcon from '@material-ui/icons/Call';
import CallEndIcon from '@material-ui/icons/CallEnd';
import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import VideocamIcon from '@material-ui/icons/Videocam';
import VideocamOffIcon from '@material-ui/icons/VideocamOff';
import type { Socket } from 'socket.io-client';

import api from '../../../../services/api';

// Fase 3/4 — ligação de voz e vídeo WebRTC. A mídia é ponta a ponta; o
// socket só carrega a sinalização. O canal de vídeo é negociado desde o
// início (transceiver vazio), então "ligar a câmera" no meio de uma
// chamada de voz é só plugar o track — sem renegociação.

type CallKind = 'audio' | 'video';

type CallPhase =
  | { kind: 'idle' }
  | {
      kind: 'incoming';
      callId: string;
      fromDocument: string;
      fromName: string;
      sdp: any;
      callKind: CallKind;
    }
  | {
      kind: 'outgoing';
      callId: string;
      toDocument: string;
      toName: string;
      callKind: CallKind;
    }
  | {
      kind: 'active';
      callId: string;
      peerDocument: string;
      peerName: string;
      callKind: CallKind;
    };

interface IProps {
  socket: Socket | null;
  outgoingTo: { document: string; name: string; kind: CallKind } | null;
  onOutgoingHandled: () => void;
}

export const CallManager = ({ socket, outgoingTo, onOutgoingHandled }: IProps) => {
  const [phase, setPhase] = useState<CallPhase>({ kind: 'idle' });
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [localVideoOn, setLocalVideoOn] = useState(false);
  const [remoteVideoOn, setRemoteVideoOn] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const phaseRef = useRef<CallPhase>(phase);
  phaseRef.current = phase;

  const cleanup = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    remoteStreamRef.current = null;
    startedAtRef.current = null;
    setMuted(false);
    setElapsed(0);
    setLocalVideoOn(false);
    setRemoteVideoOn(false);
    setPhase({ kind: 'idle' });
  }, []);

  const buildPeer = useCallback(
    async (callId: string, peerDocument: string, withVideo: boolean) => {
      const config = await api
        .get('/chat/rtc-config')
        .then((r) => r.data)
        .catch(() => ({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }));

      const pc = new RTCPeerConnection(config);
      pcRef.current = pc;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: withVideo,
      });
      streamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      // canal de vídeo sempre negociado — a câmera pode entrar depois
      if (!withVideo) {
        pc.addTransceiver('video', { direction: 'sendrecv' });
      }
      if (withVideo) {
        setLocalVideoOn(true);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      }

      const remote = new MediaStream();
      remoteStreamRef.current = remote;
      pc.ontrack = (event) => {
        remote.addTrack(event.track);
        if (audioRef.current && event.track.kind === 'audio') {
          audioRef.current.srcObject = remote;
          audioRef.current.play().catch(() => undefined);
        }
        if (remoteVideoRef.current && event.track.kind === 'video') {
          remoteVideoRef.current.srcObject = remote;
        }
      };
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket?.emit('call:ice', {
            toDocument: peerDocument,
            callId,
            candidate: event.candidate,
          });
        }
      };
      return pc;
    },
    [socket],
  );

  const currentKind = (): CallKind =>
    phaseRef.current.kind === 'idle'
      ? 'audio'
      : (phaseRef.current as any).callKind || 'audio';

  const endCall = useCallback(
    (reason: string) => {
      const current = phaseRef.current;
      if (current.kind === 'idle') return;
      const peer =
        current.kind === 'incoming'
          ? current.fromDocument
          : current.kind === 'outgoing'
            ? current.toDocument
            : current.peerDocument;
      const duration = startedAtRef.current
        ? Math.round((Date.now() - startedAtRef.current) / 1000)
        : 0;
      socket?.emit('call:end', {
        toDocument: peer,
        callId: current.callId,
        reason,
        durationSeconds: duration,
        kind: currentKind(),
        driverDocument: peer, // no portal, o outro lado é sempre o motorista
      });
      cleanup();
    },
    [socket, cleanup],
  );

  // iniciar ligação a partir dos botões do thread
  useEffect(() => {
    if (!outgoingTo || !socket) return;
    onOutgoingHandled();
    if (phaseRef.current.kind !== 'idle') return;

    const callId =
      (window.crypto as any).randomUUID?.() || `call-${Date.now()}`;

    (async () => {
      try {
        const pc = await buildPeer(
          callId,
          outgoingTo.document,
          outgoingTo.kind === 'video',
        );
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('call:invite', {
          toDocument: outgoingTo.document,
          callId,
          kind: outgoingTo.kind,
          sdp: offer,
        });
        setPhase({
          kind: 'outgoing',
          callId,
          toDocument: outgoingTo.document,
          toName: outgoingTo.name,
          callKind: outgoingTo.kind,
        });
      } catch {
        window.alert(
          'Não foi possível acessar microfone/câmera. Verifique as permissões do navegador.',
        );
        cleanup();
      }
    })();
  }, [outgoingTo, socket, buildPeer, cleanup, onOutgoingHandled]);

  // eventos de sinalização
  useEffect(() => {
    if (!socket) return;

    const onInvite = (data: any) => {
      if (phaseRef.current.kind !== 'idle') {
        socket.emit('call:end', {
          toDocument: data.fromDocument,
          callId: data.callId,
          reason: 'busy',
          driverDocument: data.fromDocument,
          durationSeconds: 0,
        });
        return;
      }
      setPhase({
        kind: 'incoming',
        callId: data.callId,
        fromDocument: data.fromDocument,
        fromName: data.fromName || data.fromDocument,
        sdp: data.sdp,
        callKind: data.kind === 'video' ? 'video' : 'audio',
      });
    };

    const onAnswer = async (data: any) => {
      const current = phaseRef.current;
      if (current.kind !== 'outgoing' || current.callId !== data.callId) return;
      await pcRef.current?.setRemoteDescription(
        new RTCSessionDescription(data.sdp),
      );
      startedAtRef.current = Date.now();
      if (current.callKind === 'video') setRemoteVideoOn(true);
      setPhase({
        kind: 'active',
        callId: current.callId,
        peerDocument: current.toDocument,
        peerName: current.toName,
        callKind: current.callKind,
      });
    };

    const onIce = (data: any) => {
      const current = phaseRef.current;
      if (current.kind === 'idle' || current.callId !== data.callId) return;
      pcRef.current
        ?.addIceCandidate(new RTCIceCandidate(data.candidate))
        .catch(() => undefined);
    };

    const onVideo = (data: any) => {
      const current = phaseRef.current;
      if (current.kind === 'idle' || current.callId !== data.callId) return;
      setRemoteVideoOn(!!data.on);
    };

    const onEnd = (data: any) => {
      const current = phaseRef.current;
      if (current.kind === 'idle' || current.callId !== data.callId) return;
      cleanup();
    };

    socket.on('call:invite', onInvite);
    socket.on('call:answer', onAnswer);
    socket.on('call:ice', onIce);
    socket.on('call:video', onVideo);
    socket.on('call:end', onEnd);
    return () => {
      socket.off('call:invite', onInvite);
      socket.off('call:answer', onAnswer);
      socket.off('call:ice', onIce);
      socket.off('call:video', onVideo);
      socket.off('call:end', onEnd);
    };
  }, [socket, cleanup]);

  const answer = useCallback(async () => {
    const current = phaseRef.current;
    if (current.kind !== 'incoming') return;
    try {
      const pc = await buildPeer(
        current.callId,
        current.fromDocument,
        current.callKind === 'video',
      );
      await pc.setRemoteDescription(new RTCSessionDescription(current.sdp));
      const sdpAnswer = await pc.createAnswer();
      await pc.setLocalDescription(sdpAnswer);
      socket?.emit('call:answer', {
        toDocument: current.fromDocument,
        callId: current.callId,
        sdp: sdpAnswer,
      });
      startedAtRef.current = Date.now();
      if (current.callKind === 'video') setRemoteVideoOn(true);
      setPhase({
        kind: 'active',
        callId: current.callId,
        peerDocument: current.fromDocument,
        peerName: current.fromName,
        callKind: current.callKind,
      });
    } catch {
      endCall('failed');
    }
  }, [socket, buildPeer, endCall]);

  /// Liga/desliga a câmera no meio da chamada (replaceTrack, sem renegociar)
  const toggleCamera = useCallback(async () => {
    const pc = pcRef.current;
    const current = phaseRef.current;
    if (!pc || current.kind === 'idle') return;
    const peer =
      current.kind === 'incoming'
        ? current.fromDocument
        : current.kind === 'outgoing'
          ? current.toDocument
          : current.peerDocument;

    const videoSender = pc
      .getTransceivers()
      .find((t) => t.receiver.track?.kind === 'video')?.sender;
    if (!videoSender) return;

    if (localVideoOn) {
      videoSender.track?.stop();
      await videoSender.replaceTrack(null);
      setLocalVideoOn(false);
      socket?.emit('call:video', {
        toDocument: peer,
        callId: current.callId,
        on: false,
      });
      return;
    }

    try {
      const camera = await navigator.mediaDevices.getUserMedia({ video: true });
      const track = camera.getVideoTracks()[0];
      await videoSender.replaceTrack(track);
      streamRef.current?.addTrack(track);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = camera;
      }
      setLocalVideoOn(true);
      socket?.emit('call:video', {
        toDocument: peer,
        callId: current.callId,
        on: true,
      });
    } catch {
      window.alert('Sem acesso à câmera — verifique a permissão do navegador.');
    }
  }, [socket, localVideoOn]);

  // cronômetro
  useEffect(() => {
    if (phase.kind !== 'active') return;
    const timer = setInterval(
      () =>
        setElapsed(
          startedAtRef.current
            ? Math.round((Date.now() - startedAtRef.current) / 1000)
            : 0,
        ),
      1000,
    );
    return () => clearInterval(timer);
  }, [phase.kind]);

  // chamada não atendida em 45s
  useEffect(() => {
    if (phase.kind !== 'outgoing') return;
    const timeout = setTimeout(() => endCall('missed'), 45000);
    return () => clearTimeout(timeout);
  }, [phase, endCall]);

  const toggleMute = () => {
    const track = streamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = muted;
    setMuted(!muted);
  };

  const format = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const name =
    phase.kind === 'incoming'
      ? phase.fromName
      : phase.kind === 'outgoing'
        ? phase.toName
        : phase.kind === 'active'
          ? phase.peerName
          : '';

  const showVideoArea =
    phase.kind !== 'idle' && (remoteVideoOn || localVideoOn);

  return (
    <>
      {/* áudio remoto (sempre montado) */}
      <audio ref={audioRef} autoPlay />
      <Dialog
        open={phase.kind !== 'idle'}
        maxWidth={showVideoArea ? 'md' : 'xs'}
        fullWidth
      >
        <Box
          p={showVideoArea ? 2 : 4}
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{ gap: 12 }}
        >
          {showVideoArea ? (
            <div
              style={{
                position: 'relative',
                width: '100%',
                background: '#000',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxHeight: '60vh',
                  display: remoteVideoOn ? 'block' : 'none',
                }}
              />
              {!remoteVideoOn && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{ height: 240, color: '#fff' }}
                >
                  <Typography variant="body2">
                    {name} está sem câmera
                  </Typography>
                </Box>
              )}
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  width: 140,
                  borderRadius: 8,
                  border: '2px solid rgba(255,255,255,0.6)',
                  display: localVideoOn ? 'block' : 'none',
                }}
              />
            </div>
          ) : (
            <Avatar style={{ width: 72, height: 72, background: '#0E52A0' }}>
              {(name || '?').charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {phase.kind === 'incoming' &&
              (phase.callKind === 'video'
                ? 'Vídeo chamada recebida...'
                : 'Ligação recebida...')}
            {phase.kind === 'outgoing' && 'Chamando...'}
            {phase.kind === 'active' && format(elapsed)}
          </Typography>
          <Box display="flex" style={{ gap: 20, marginTop: 8 }}>
            {phase.kind === 'incoming' && (
              <IconButton
                onClick={answer}
                style={{ background: '#48AE3D', color: '#fff' }}
                aria-label="Atender"
              >
                {phase.callKind === 'video' ? <VideocamIcon /> : <CallIcon />}
              </IconButton>
            )}
            {phase.kind === 'active' && (
              <>
                <IconButton
                  onClick={toggleMute}
                  style={{
                    background: muted ? '#B7791F' : '#eee',
                    color: muted ? '#fff' : '#333',
                  }}
                  aria-label="Mudo"
                >
                  {muted ? <MicOffIcon /> : <MicIcon />}
                </IconButton>
                <IconButton
                  onClick={toggleCamera}
                  style={{
                    background: localVideoOn ? '#0E52A0' : '#eee',
                    color: localVideoOn ? '#fff' : '#333',
                  }}
                  aria-label="Câmera"
                  title={
                    localVideoOn ? 'Desligar câmera' : 'Ligar câmera'
                  }
                >
                  {localVideoOn ? <VideocamIcon /> : <VideocamOffIcon />}
                </IconButton>
              </>
            )}
            <IconButton
              onClick={() =>
                endCall(
                  phase.kind === 'incoming'
                    ? 'declined'
                    : phase.kind === 'outgoing'
                      ? 'missed'
                      : 'ended',
                )
              }
              style={{ background: '#C43D3D', color: '#fff' }}
              aria-label="Encerrar"
            >
              <CallEndIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};
